import React, { useState, useEffect } from "react";
import BotonManualHeader from "../../components/BotonManualHeader";
import {
  Container,
  Card,
  Table,
  Button,
  Badge,
  Row,
  Col,
  Form,
  OverlayTrigger,
  Tooltip,
  Spinner,
} from "react-bootstrap";
import {
  collection,
  getDocs,
  getFirestore,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import {
  FaChartLine,
  FaEye,
  FaEdit,
  FaUsers,
  FaClipboardList,
  FaPlus,
  FaSync,
  FaSearch,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaFilter,
  FaChartBar,
} from "react-icons/fa";
import {
  BsCheckCircleFill,
  BsFillXCircleFill,
  BsClockFill,
} from "react-icons/bs";
import firebaseApp from "../../firebase/credenciales";

const db = getFirestore(firebaseApp);

const formatearFecha = (timestamp) => {
  if (!timestamp) return "";
  
  let date;
  
  // Si es un timestamp de Firestore
  if (timestamp.seconds) {
    date = new Date(timestamp.seconds * 1000);
  } 
  // Si ya es un objeto Date
  else if (timestamp instanceof Date) {
    date = timestamp;
  }
  // Si es un string o número
  else {
    date = new Date(timestamp);
  }
  
  // Validar que la fecha sea válida
  if (isNaN(date.getTime())) {
    return "";
  }
  
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const DashboardPage = ({ usuario }) => {
  const [campanas, setCampanas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("TODOS");
  const navigate = useNavigate();

  useEffect(() => {
    if (usuario?.ruc) {
      cargarDatos();
    }
  }, [usuario]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const rucEmpresa = usuario?.ruc || "";
      
      console.log("🔍 DEBUG - Cargando datos para RUC:", rucEmpresa);

      // Cargar campañas de evaluación (con ñ)
      const campanasRef = collection(db, `evaluaciones/${rucEmpresa}/campañas`);
      const campanasSnapshot = await getDocs(campanasRef);
      const campanasData = [];
      
      console.log("📊 DEBUG - Documentos encontrados:", campanasSnapshot.size);

      campanasSnapshot.forEach((doc) => {
        const data = doc.data();
        console.log("📄 DEBUG - Documento:", doc.id, data);
        
        // Manejar diferentes estructuras de fechas
        let fechaInicio = data.fechaInicio;
        let fechaFin = data.fechaFin;
        
        // Si viene en formato string "dd/mm/yyyy - dd/mm/yyyy", parsear
        if (data.fechas && typeof data.fechas === 'string') {
          const [inicio, fin] = data.fechas.split(' - ');
          console.log("📅 DEBUG - Parseando fechas string:", inicio, "-", fin);
          if (inicio && fin) {
            // Convertir dd/mm/yyyy a Date
            const [diaIni, mesIni, añoIni] = inicio.trim().split('/');
            const [diaFin, mesFin, añoFin] = fin.trim().split('/');
            fechaInicio = new Date(añoIni, mesIni - 1, diaIni);
            fechaFin = new Date(añoFin, mesFin - 1, diaFin);
            console.log("✅ DEBUG - Fechas convertidas:", fechaInicio, fechaFin);
          }
        }

        // Contar evaluados de diferentes estructuras
        const evaluados = data.evaluados || data.evaluaciones || [];
        console.log("👥 DEBUG - Evaluados encontrados:", evaluados.length);
        
        const campana = {
          id: doc.id,
          nombre: data.nombre || "Sin nombre",
          tipo: data.tipoEvaluacion || "180",
          fechaInicio: fechaInicio,
          fechaFin: fechaFin,
          evaluados: evaluados,
          estado: calcularEstado(fechaInicio, fechaFin, data.estado),
          totalEvaluados: evaluados.length,
          completados: evaluados.filter(
            (e) => e.estado === "completado" || e.estado === "completada"
          ).length,
        };
        
        console.log("✨ DEBUG - Campaña procesada:", campana);
        campanasData.push(campana);
      });

      console.log("🎯 DEBUG - Total campañas procesadas:", campanasData.length);

      setCampanas(
        campanasData.sort((a, b) => {
          const getFecha = (fecha) => {
            if (!fecha) return 0;
            if (fecha.seconds) return fecha.seconds;
            if (fecha instanceof Date) return fecha.getTime() / 1000;
            return 0;
          };
          
          const fechaA = getFecha(a.fechaInicio);
          const fechaB = getFecha(b.fechaInicio);
          return fechaB - fechaA;
        })
      );
    } catch (error) {
      console.error("❌ ERROR al cargar datos:", error);
    } finally {
      setLoading(false);
    }
  };

  const calcularEstado = (fechaInicio, fechaFin, estadoGuardado) => {
    // Si ya tiene un estado guardado válido, usarlo
    if (estadoGuardado) {
      const estadoUpper = estadoGuardado.toUpperCase();
      if (['ACTIVA', 'PROGRAMADA', 'FINALIZADA', 'BORRADOR'].includes(estadoUpper)) {
        return estadoUpper;
      }
    }

    if (!fechaInicio || !fechaFin) return "BORRADOR";

    const ahora = new Date();
    
    // Convertir fechaInicio a Date si es necesario
    let inicio = fechaInicio;
    if (fechaInicio.seconds) {
      inicio = new Date(fechaInicio.seconds * 1000);
    } else if (!(fechaInicio instanceof Date)) {
      inicio = new Date(fechaInicio);
    }
    
    // Convertir fechaFin a Date si es necesario
    let fin = fechaFin;
    if (fechaFin.seconds) {
      fin = new Date(fechaFin.seconds * 1000);
    } else if (!(fechaFin instanceof Date)) {
      fin = new Date(fechaFin);
    }

    if (ahora < inicio) return "PROGRAMADA";
    if (ahora > fin) return "FINALIZADA";
    return "ACTIVA";
  };

  const getBadgeVariant = (estado) => {
    switch (estado?.toUpperCase()) {
      case "ACTIVA":
        return "success";
      case "PROGRAMADA":
        return "info";
      case "FINALIZADA":
        return "secondary";
      case "BORRADOR":
        return "warning";
      default:
        return "secondary";
    }
  };

  const getTipoLabel = (tipo) => {
    const tipos = {
      "90": "90°",
      "180": "180°",
      "360": "360°",
    };
    return tipos[tipo] || tipo;
  };

  // Filtros
  const campanasFiltradas = campanas.filter((campana) => {
    const cumpleBusqueda = campana.nombre
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const cumpleEstado =
      filtroEstado === "TODOS" || campana.estado === filtroEstado;
    return cumpleBusqueda && cumpleEstado;
  });

  // Estadísticas
  const calcularEstadisticas = () => {
    return {
      total: campanas.length,
      activas: campanas.filter((c) => c.estado === "ACTIVA").length,
      programadas: campanas.filter((c) => c.estado === "PROGRAMADA").length,
      finalizadas: campanas.filter((c) => c.estado === "FINALIZADA").length,
      totalEvaluados: campanas.reduce((sum, c) => sum + c.totalEvaluados, 0),
      totalCompletados: campanas.reduce((sum, c) => sum + c.completados, 0),
    };
  };

  const estadisticas = calcularEstadisticas();

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        {/* Indicador de carga */}
        {loading && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-75"
            style={{ zIndex: 1080 }}
          >
            <div className="text-center">
              <Spinner
                animation="border"
                variant="light"
                style={{ width: "3rem", height: "3rem" }}
              />
              <p className="text-white mt-3 mb-0">Cargando campañas...</p>
            </div>
          </div>
        )}

        {/* Marco principal */}
        <Card
          className="shadow-lg border-0"
          style={{
            borderRadius: "15px",
            overflow: "hidden",
            minHeight: "90vh",
          }}
        >
          {/* Header con degradado naranja */}
          <Card.Header
            className="text-white py-4 position-relative"
            style={{
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
              borderBottom: "3px solid #ff8a80",
            }}
          >
            <div className="position-absolute end-0 top-50 translate-middle-y me-4" style={{ zIndex: 10 }}>
              <BotonManualHeader usuario={usuario} />
            </div>
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <FaChartLine className="me-3" />
                Evaluación de Desempeño
              </h2>
              <p className="mb-0 opacity-75">
                Gestiona campañas de evaluación y analiza resultados
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Barra superior con acciones */}
            <Card
              className="shadow-sm border-0 mb-4"
              style={{ borderRadius: "15px" }}
            >
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col md={1}>
                    <div
                      className="rounded-3 p-3 text-center"
                      style={{
                        background:
                          "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                        color: "white",
                      }}
                    >
                      <FaClipboardList size={24} />
                    </div>
                  </Col>
                  <Col md={7}>
                    <h5 className="mb-1 fw-semibold">
                      Dashboard de Evaluaciones
                    </h5>
                    <p className="text-muted mb-0 small">
                      Gestiona campañas de evaluación 90°, 180° y 360°
                    </p>
                  </Col>
                  <Col md={4} className="text-end">
                    <div className="d-flex gap-2 justify-content-end">
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Crear nueva campaña</Tooltip>}
                      >
                        <Button
                          className="d-flex align-items-center gap-2 text-white"
                          onClick={() => navigate("/evaluaciones/crear-campana")}
                          style={{
                            borderRadius: "10px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            background:
                              "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                            border: "none",
                          }}
                        >
                          <FaPlus size="16px" />
                          Nueva Campaña
                        </Button>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Actualizar lista</Tooltip>}
                      >
                        <Button
                          variant="outline-secondary"
                          className="d-flex align-items-center gap-2"
                          onClick={cargarDatos}
                          style={{
                            borderRadius: "10px",
                            borderColor: "#f093fb",
                            color: "#f093fb",
                          }}
                        >
                          <FaSync size="16px" />
                          Actualizar
                        </Button>
                      </OverlayTrigger>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Estadísticas - KPI */}
            <Row className="mb-4">
              <Col md={3}>
                <Card
                  className="border-0 shadow-sm"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body
                    className="text-center"
                    style={{
                      background:
                        "linear-gradient(135deg, #17a2b8 0%, #20c997 100%)",
                      color: "white",
                      borderRadius: "15px",
                    }}
                  >
                    <FaClipboardList size={32} className="mb-2" />
                    <h4 className="fw-bold">{estadisticas.total}</h4>
                    <p className="mb-0">Total Campañas</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card
                  className="border-0 shadow-sm"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body
                    className="text-center"
                    style={{
                      background:
                        "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                      color: "white",
                      borderRadius: "15px",
                    }}
                  >
                    <BsCheckCircleFill size={32} className="mb-2" />
                    <h4 className="fw-bold">{estadisticas.activas}</h4>
                    <p className="mb-0">Campañas Activas</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card
                  className="border-0 shadow-sm"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body
                    className="text-center"
                    style={{
                      background:
                        "linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)",
                      color: "white",
                      borderRadius: "15px",
                    }}
                  >
                    <BsClockFill size={32} className="mb-2" />
                    <h4 className="fw-bold">{estadisticas.programadas}</h4>
                    <p className="mb-0">Programadas</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card
                  className="border-0 shadow-sm"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body
                    className="text-center"
                    style={{
                      background:
                        "linear-gradient(135deg, #007bff 0%, #6610f2 100%)",
                      color: "white",
                      borderRadius: "15px",
                    }}
                  >
                    <FaCheckCircle size={32} className="mb-2" />
                    <h4 className="fw-bold">{estadisticas.finalizadas}</h4>
                    <p className="mb-0">Finalizadas</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Barra de filtros */}
            <Card
              className="shadow-sm border-0 mb-4"
              style={{ borderRadius: "15px" }}
            >
              <Card.Body className="p-3">
                <Row className="g-2 align-items-center">
                  <Col md={6}>
                    <div className="position-relative">
                      <FaSearch
                        className="position-absolute"
                        style={{
                          left: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#6c757d",
                          zIndex: 2,
                        }}
                      />
                      <Form.Control
                        type="text"
                        placeholder="Buscar campañas por nombre..."
                        className="form-control-lg ps-5"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                          borderRadius: "10px",
                          border: "2px solid #e9ecef",
                        }}
                      />
                    </div>
                  </Col>
                  <Col md={4}>
                    <Form.Select
                      value={filtroEstado}
                      onChange={(e) => setFiltroEstado(e.target.value)}
                      className="form-control-lg"
                      style={{
                        borderRadius: "10px",
                        border: "2px solid #e9ecef",
                      }}
                    >
                      <option value="TODOS">Todos los estados</option>
                      <option value="ACTIVA">Activas</option>
                      <option value="PROGRAMADA">Programadas</option>
                      <option value="FINALIZADA">Finalizadas</option>
                      <option value="BORRADOR">Borradores</option>
                    </Form.Select>
                  </Col>
                  <Col md={2}>
                    <Button
                      variant="outline-secondary"
                      size="lg"
                      className="w-100 d-flex align-items-center justify-content-center gap-2"
                      onClick={() => {
                        setSearchTerm("");
                        setFiltroEstado("TODOS");
                      }}
                      style={{ borderRadius: "10px" }}
                    >
                      <FaSync size="16px" />
                      Limpiar
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Mensaje cuando no hay campañas */}
            {campanasFiltradas.length === 0 && !loading && (
              <Card
                className="border-0 shadow-sm mb-4"
                style={{ borderRadius: "15px" }}
              >
                <Card.Body className="text-center py-5">
                  <div
                    className="bg-light rounded-circle mx-auto mb-3"
                    style={{
                      width: "80px",
                      height: "80px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FaClipboardList size={32} className="text-muted" />
                  </div>
                  <h5 className="text-muted mb-2">
                    No hay campañas disponibles
                  </h5>
                  <p className="text-muted mb-3">
                    {searchTerm || filtroEstado !== "TODOS"
                      ? "No se encontraron campañas que coincidan con los filtros aplicados"
                      : "Crea tu primera campaña de evaluación haciendo clic en 'Nueva Campaña'"}
                  </p>
                  <div className="d-flex gap-2 justify-content-center">
                    {(searchTerm || filtroEstado !== "TODOS") && (
                      <Button
                        variant="outline-secondary"
                        onClick={() => {
                          setSearchTerm("");
                          setFiltroEstado("TODOS");
                        }}
                        className="d-flex align-items-center gap-2"
                        style={{ borderRadius: "10px" }}
                      >
                        <FaSync size="14px" />
                        Limpiar filtros
                      </Button>
                    )}
                    <Button
                      className="d-flex align-items-center gap-2 text-white"
                      onClick={() => navigate("/evaluaciones/crear-campana")}
                      style={{
                        borderRadius: "10px",
                        background:
                          "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                        border: "none",
                      }}
                    >
                      <FaPlus size="14px" />
                      Nueva Campaña
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Tabla de campañas */}
            {campanasFiltradas.length > 0 && (
              <Card
                className="border-0 shadow-sm mb-4"
                style={{ borderRadius: "15px" }}
              >
                <Card.Header
                  className="text-white py-3"
                  style={{
                    background:
                      "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                    borderRadius: "15px 15px 0 0",
                  }}
                >
                  <div className="d-flex align-items-center">
                    <FaClipboardList className="me-2" />
                    <div className="flex-grow-1">
                      <h5 className="mb-0 fw-semibold">
                        Lista de Campañas
                      </h5>
                      <small className="opacity-75">
                        Gestiona y supervisa las campañas de evaluación
                      </small>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <Badge
                        bg="light"
                        text="dark"
                        style={{ fontSize: "0.9rem" }}
                      >
                        {campanasFiltradas.length} campañas
                      </Badge>
                      {(filtroEstado !== "TODOS" || searchTerm) && (
                        <Badge bg="warning" style={{ fontSize: "0.8rem" }}>
                          Filtros aplicados
                        </Badge>
                      )}
                    </div>
                  </div>
                </Card.Header>

                <Card.Body className="p-0">
                  <div style={{ maxHeight: "600px", overflowY: "auto" }}>
                    <Table
                      hover
                      responsive
                      className="mb-0"
                      style={{ fontSize: "0.9rem" }}
                    >
                      <thead
                        style={{
                          backgroundColor: "#f8f9fa",
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                        }}
                      >
                        <tr>
                          <th
                            className="border-0 py-3 px-3 text-secondary fw-semibold text-center"
                            style={{ width: "4%" }}
                          >
                            #
                          </th>
                          <th
                            className="border-0 py-3 px-3 text-secondary fw-semibold"
                            style={{ width: "30%" }}
                          >
                            <FaClipboardList className="me-1" />
                            Nombre
                          </th>
                          <th
                            className="border-0 py-3 px-3 text-secondary fw-semibold text-center"
                            style={{ width: "8%" }}
                          >
                            Tipo
                          </th>
                          <th
                            className="border-0 py-3 px-3 text-secondary fw-semibold"
                            style={{ width: "12%" }}
                          >
                            Período
                          </th>
                          <th
                            className="border-0 py-3 px-3 text-secondary fw-semibold text-center"
                            style={{ width: "10%" }}
                          >
                            Estado
                          </th>
                          <th
                            className="border-0 py-3 px-3 text-secondary fw-semibold text-center"
                            style={{ width: "12%" }}
                          >
                            <FaUsers className="me-1" />
                            Evaluados
                          </th>
                          <th
                            className="border-0 py-3 px-3 text-secondary fw-semibold text-center"
                            style={{ width: "12%" }}
                          >
                            Progreso
                          </th>
                          <th
                            className="border-0 py-3 px-3 text-secondary fw-semibold text-center"
                            style={{ width: "12%" }}
                          >
                            Acciones
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {campanasFiltradas.map((campana, index) => (
                          <tr
                            key={campana.id}
                            style={{ borderBottom: "1px solid #f1f3f4" }}
                          >
                            <td className="text-center py-3 px-3">
                              <Badge
                                style={{
                                  fontSize: "0.75rem",
                                  background:
                                    "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                                  border: "none",
                                }}
                                className="px-2 py-1"
                              >
                                {index + 1}
                              </Badge>
                            </td>
                            <td className="py-3 px-3">
                              <div className="fw-bold text-dark">
                                {campana.nombre}
                              </div>
                            </td>
                            <td className="text-center py-3 px-3">
                              <Badge
                                bg="info"
                                style={{ fontSize: "0.75rem" }}
                                className="px-2 py-1"
                              >
                                {getTipoLabel(campana.tipo)}
                              </Badge>
                            </td>
                            <td className="py-3 px-3">
                              <small className="text-muted d-block">
                                <FaCalendarAlt className="me-1" />
                                {formatearFecha(campana.fechaInicio)}
                              </small>
                              <small className="text-muted">
                                hasta {formatearFecha(campana.fechaFin)}
                              </small>
                            </td>
                            <td className="text-center py-3 px-3">
                              <Badge
                                bg={getBadgeVariant(campana.estado)}
                                style={{ fontSize: "0.75rem" }}
                                className="px-2 py-1"
                              >
                                {campana.estado}
                              </Badge>
                            </td>
                            <td className="text-center py-3 px-3">
                              <span
                                className="fw-semibold"
                                style={{ color: "#f093fb" }}
                              >
                                {campana.totalEvaluados || 0}
                              </span>
                            </td>
                            <td className="text-center py-3 px-3">
                              <div className="d-flex flex-column align-items-center">
                                <small className="text-muted mb-1">
                                  {campana.completados || 0} /{" "}
                                  {campana.totalEvaluados || 0}
                                </small>
                                <div
                                  className="progress"
                                  style={{ width: "100%", height: "6px" }}
                                >
                                  <div
                                    className="progress-bar bg-success"
                                    style={{
                                      width: `${
                                        campana.totalEvaluados > 0
                                          ? (campana.completados /
                                              campana.totalEvaluados) *
                                            100
                                          : 0
                                      }%`,
                                    }}
                                  />
                                </div>
                              </div>
                            </td>
                            <td className="text-center py-3 px-3">
                              <div className="d-flex gap-1 justify-content-center flex-wrap">
                                <OverlayTrigger
                                  placement="top"
                                  overlay={<Tooltip>Ver campaña</Tooltip>}
                                >
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() =>
                                      navigate(
                                        `/evaluaciones/campana/${campana.id}`
                                      )
                                    }
                                    style={{
                                      borderRadius: "6px",
                                      padding: "4px 8px",
                                      borderColor: "#f093fb",
                                      color: "#f093fb",
                                    }}
                                  >
                                    <FaEye size="12px" />
                                  </Button>
                                </OverlayTrigger>

                                <OverlayTrigger
                                  placement="top"
                                  overlay={<Tooltip>Editar campaña</Tooltip>}
                                >
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() =>
                                      navigate(
                                        `/evaluaciones/editar-campana/${campana.id}`
                                      )
                                    }
                                    style={{
                                      borderRadius: "6px",
                                      padding: "4px 8px",
                                      borderColor: "#f5576c",
                                      color: "#f5576c",
                                    }}
                                  >
                                    <FaEdit size="12px" />
                                  </Button>
                                </OverlayTrigger>

                                <OverlayTrigger
                                  placement="top"
                                  overlay={<Tooltip>Ver resultados</Tooltip>}
                                >
                                  <Button
                                    variant="outline-success"
                                    size="sm"
                                    onClick={() =>
                                      navigate(
                                        `/evaluaciones/resultados/${campana.id}`
                                      )
                                    }
                                    style={{
                                      borderRadius: "6px",
                                      padding: "4px 8px",
                                      borderColor: "#28a745",
                                      color: "#28a745",
                                    }}
                                  >
                                    <FaChartBar size="12px" />
                                  </Button>
                                </OverlayTrigger>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Estadísticas adicionales */}
            <Row>
              <Col md={6}>
                <Card
                  className="border-0 shadow-sm"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body className="p-4 text-center">
                    <FaUsers
                      size={32}
                      className="mb-3"
                      style={{ color: "#17a2b8" }}
                    />
                    <h4 className="fw-bold" style={{ color: "#17a2b8" }}>
                      {estadisticas.totalEvaluados}
                    </h4>
                    <p className="text-muted mb-0">Total de Evaluados</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card
                  className="border-0 shadow-sm"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body className="p-4 text-center">
                    <FaCheckCircle size={32} className="text-success mb-3" />
                    <h4 className="fw-bold text-success">
                      {estadisticas.totalCompletados}
                    </h4>
                    <p className="text-muted mb-0">Evaluaciones Completadas</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default DashboardPage;
